import React, { useState } from 'react';
import EditModalTemplate from '@component/gridgio/modal/dialog-modal/edit-modal-template';
import TextBlock from '@component/common/block/text-block';
import TextInput from '@component/common/input/text-input';
import TextAreaInput from '@component/common/input/text-area-input';
import Gap from '@component/common/gap';
import { useTypedDispatch } from '@hook/common/useStore';
import { useModifyWorkMutation } from '@hook/gridgio/useApi';
import { actions as modalActions } from '@store/slice/gridgio/modal-slice';
import type { IWorkEditModalData } from '@datatype/gridgio/type';
import * as color from '@constant/color';
import * as size from '@constant/size';

interface IProps {
  data: IWorkEditModalData['data'];
}

const LABEL_DESIGN = {
  box: { width: '100%', alignX: 'left' },
  text: { size: 'medium', color: color.BLACK_2, bold: true },
} as const;

const INPUT_DESIGN = {
  box: {
    width: '100%',
    height: '42px',
    padding: `0 ${size.MARGIN_1}`,
    border: true,
    borderWidth: size.BORDER_WIDTH_1,
    borderColor: color.GRAY_2,
    borderRadius: size.BORDER_RADIUS_1,
    alignX: 'left',
  },
  text: { size: 'medium', color: color.BLACK_2 },
} as const;

const WorkEditModal: React.FC<IProps> = ({ data }) => {
  const dispatch = useTypedDispatch();
  const mutation = useModifyWorkMutation();
  const [name, setName] = useState(data.name);
  const [description, setDescription] = useState(data.description ?? '');
  const isChanged = name.trim() !== data.name || description.trim() !== (data.description ?? '');

  const handleCancel = () => {
    if (!mutation.isPending) dispatch(modalActions.hideModal());
  };
  const handleSave = async () => {
    if (!name.trim() || !isChanged || mutation.isPending) return;
    try {
      await mutation.mutateAsync({ workKey: data.workKey, name: name.trim(), description: description.trim() });
      dispatch(modalActions.hideModal());
    } catch {}
  };

  return (
    <EditModalTemplate
      title='작업 편집'
      isPending={mutation.isPending}
      saveDisabled={!name.trim() || !isChanged}
      handleCancel={handleCancel}
      handleSave={handleSave}
      input={
        <>
          <TextBlock text='작업 이름' design={LABEL_DESIGN} />
          <Gap height={size.MARGIN_1} />
          <TextInput
            name='work-name'
            value={name}
            placeholder='작업 이름을 입력해주세요.'
            design={INPUT_DESIGN}
            onChange={(event) => {
              if (!mutation.isPending) setName(event.target.value);
            }}
          />
          <Gap height={size.MARGIN_2} />
          <TextBlock text='작업 설명 (선택)' design={LABEL_DESIGN} />
          <Gap height={size.MARGIN_1} />
          <TextAreaInput
            name='work-description'
            value={description}
            placeholder='작업 설명을 입력해주세요.'
            row={3}
            max={500}
            design={{ ...INPUT_DESIGN, box: { ...INPUT_DESIGN.box, height: '80px', padding: size.MARGIN_1 } }}
            onChange={(event) => {
              if (!mutation.isPending) setDescription(event.target.value);
            }}
          />
        </>
      }
    />
  );
};

export default WorkEditModal;
